import { Button, DatePicker, Input, message, Modal } from "antd";
import type { Port, ShipFollowCustomVo } from "@zhihaoscm/entity";
import { SHIP_SHOW_STATUS } from "@zhihaoscm/meta";
import dayjs, { type Dayjs } from "dayjs";
import { useEffect, useRef, useState } from "react";
import { shipFollowCustomService } from "@/api/shipFollowCustomService";
import { shipService } from "@/api/shipService";
import { ShipRemotePortSelect } from "./ShipOrderFormLegacyComponents";
import notDataImage from "../../../../old-console-ui/src/assets/images/notData.png";

const EMPTY_VALUE = "--";

/** Vue shipFollow.vue 的关注船舶浮层，保留检索、进入详情和移出关注行为。 */
export function FindShipFollowPanel({ onClose, onSelect, onRemoved }: { onClose: () => void; onSelect: (shipId: string) => void; onRemoved: (shipId: string) => void }) {
    const [keyword, setKeyword] = useState("");
    const [items, setItems] = useState<ShipFollowCustomVo[]>([]);
    const [loading, setLoading] = useState(false);
    const pageRef = useRef(1);
    const totalPageRef = useRef(1);
    const searchReadyRef = useRef(false);

    /** 关注列表每次检索从第一页刷新，与 Vue refresh 一致。 */
    const load = async (nextKeyword = keyword, reset = true) => {
        if (loading || (!reset && pageRef.current >= totalPageRef.current)) return;
        const page = reset ? 1 : pageRef.current + 1;
        setLoading(true);
        try {
            const result = await shipFollowCustomService.paging({ keyword: nextKeyword, page, size: 10 });
            pageRef.current = page;
            totalPageRef.current = result.totalPage ?? page;
            setItems(current => reset ? (result.list ?? []) : [...current, ...(result.list ?? [])]);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { void load(""); }, []);
    useEffect(() => {
        if (!searchReadyRef.current) { searchReadyRef.current = true; return; }
        const timer = window.setTimeout(() => void load(keyword, true), 500);
        return () => window.clearTimeout(timer);
    }, [keyword]);

    /** 用户确认后调用旧页相同的移出关注接口，并同步当前列表。 */
    const remove = (item: ShipFollowCustomVo) => {
        Modal.confirm({
            title: "提示",
            content: "是否移出此船舶?",
            okText: "确认",
            cancelText: "取消",
            onOk: async () => {
                await shipFollowCustomService.removeAttention(item.shipFollowCustom.id);
                const index = items.findIndex(row => row.shipFollowCustom.id === item.shipFollowCustom.id);
                const reloadPage = Math.floor(Math.max(index, 0) / 10) + 1;
                const result = await shipFollowCustomService.paging({ keyword, page: reloadPage, size: 10 });
                pageRef.current = reloadPage;
                totalPageRef.current = result.totalPage ?? reloadPage;
                setItems(current => current.slice(0, (reloadPage - 1) * 10).concat(result.list ?? []));
                onRemoved(item.shipFollowCustom.shipId);
                message.success("已移出");
            }
        });
    };

    return <aside className="find-ship-follow-panel" aria-label="关注的船面板">
        <header><strong>关注的船</strong><button aria-label="关闭关注的船" type="button" onClick={onClose}><i className="iconfont icon-close" /></button></header>
        <div className="find-ship-follow-search">
            <Input allowClear prefix={<i className="iconfont icon-search1" />} value={keyword} placeholder="输入关键字进行过滤" onChange={event => setKeyword(event.target.value)} onPressEnter={() => void load()} />
        </div>
        <div className="find-ship-follow-list" aria-busy={loading} onScroll={event => { const target = event.currentTarget; if (target.scrollHeight - target.scrollTop - target.clientHeight <= 100) void load(keyword, false); }}>
            {items.length ? items.map(item => <div className="find-ship-follow-item" key={item.shipFollowCustom.id}>
                <button type="button" className="find-ship-follow-main" onClick={() => onSelect(item.shipFollowCustom.shipId)}>
                    <i className="iconfont icon-ship-1" />
                    <span><strong>{item.ship.cnname ? `${item.ship.cnname} / ` : ""}{item.ship.name}</strong><small><em>MMSI {item.ship.id}</em><em>状态 {SHIP_SHOW_STATUS.getIntroByCode(Number(item.status)) || EMPTY_VALUE}</em></small></span>
                </button>
                <button aria-label={`移出${item.ship.cnname || item.ship.name}`} type="button" onClick={() => remove(item)}><i className="iconfont icon-close" /></button>
            </div>) : <div className="find-ship-follow-empty"><img src={notDataImage} alt="" /><span>{loading ? "加载中" : "暂无数据"}</span></div>}
        </div>
    </aside>;
}

export interface RoutePlanningResult {
    distance: number;
    duration: number;
    arrivalTime: string;
    route: Array<{ lon: number; lat: number }>;
    origin: Port;
    destination: Port;
}

interface TransportComputeResult {
    distance?: number;
}

/** Vue routePlanning.vue 的航线规划浮层，调用同一规划与航程计算接口。 */
export function FindRoutePlanningPanel({ onClose, onPlanned }: { onClose: () => void; onPlanned: (result: RoutePlanningResult) => void }) {
    const [origin, setOrigin] = useState<Port>();
    const [destination, setDestination] = useState<Port>();
    const [speed, setSpeed] = useState<number | undefined>(5);
    const [departureTime, setDepartureTime] = useState<Dayjs | null>(dayjs());
    const [result, setResult] = useState<RoutePlanningResult>();
    const [loading, setLoading] = useState(false);

    /** 校验并执行旧版两段接口：先规划轨迹，再计算航程和到达时间。 */
    const plan = async () => {
        if (!origin || !destination) { message.warning("请先选择始发地跟目的地！"); return; }
        if (origin.id === destination.id) { message.warning("始发地和目的地不能相同，请重新选择"); return; }
        if (!speed || !departureTime) { message.warning("请先填写预计航速及预计出发时间！"); return; }
        // Port.latLon 与旧 Vue/API 的统一合同是“经度,纬度”，不可按字段名直觉颠倒。
        const [originLon, originLat] = (origin.latLon ?? "").split(",").map(Number);
        const [destinationLon, destinationLat] = (destination.latLon ?? "").split(",").map(Number);
        if ([originLat, originLon, destinationLat, destinationLon].some(Number.isNaN)) { message.error("坐标数据无效"); return; }
        setLoading(true);
        // Vue 在新一次航程计算完成前不会展示旧结果或中间态的 0 值卡片。
        setResult(undefined);
        try {
            // Vue 先完成路线绘制，再计算航程和到达时间；不能并发，否则慢接口会改变地图反馈顺序。
            const planning = await shipService.transportPlanning({ sourceId: origin.id, destinationId: destination.id });
            const route = (planning?.route ?? []).map((point: { lng?: number; lat?: number }) => ({ lon: point.lng ?? 0, lat: point.lat ?? 0 })).reverse();
            const routeResult = { distance: 0, duration: 0, arrivalTime: "", route, origin, destination };
            onPlanned(routeResult);
            message.success("航线规划完成。地图已隐藏部分元素，可手动开启，退出航线规划时将恢复之前状态。");
            try {
                const compute = await shipService.transportCompute({ sourceGeoPoint: { lng: originLon, lat: originLat }, destinationGeoPoint: { lng: destinationLon, lat: destinationLat } }) as TransportComputeResult;
                const rawDistance = Number(compute?.distance ?? 0);
                const rawDuration = rawDistance / speed;
                // 展示值保留两位，但到达时间必须沿用 Vue 的原始时长，避免累计四舍五入误差。
                const nextResult = {
                    ...routeResult,
                    distance: Math.round(rawDistance * 100) / 100,
                    duration: Math.round(rawDuration * 100) / 100,
                    arrivalTime: departureTime.add(rawDuration, "hour").format("YYYY-MM-DD HH:mm:ss")
                };
                setResult(nextResult);
            } catch {
                // 轨迹规划成功但航程计算失败时，Vue 保留轨迹并隐藏结果卡。
                setResult(undefined);
                message.error("时间计算失败，输入重试");
            }
        } finally {
            setLoading(false);
        }
    };

    return <aside className="find-route-planning-panel" aria-label="航线规划面板">
        <header><span><i />航线规划</span><button aria-label="关闭航线规划" type="button" onClick={onClose}><i className="iconfont icon-close" /></button></header>
        <div className="find-route-planning-content">
            <div className="find-route-port-fields">
                <div className="find-route-symbols"><i className="iconfont icon-beginning" /><button aria-label="交换始发地和目的地" type="button" onClick={() => { setOrigin(destination); setDestination(origin); }}><i className="iconfont icon-swap" /></button><i className="iconfont icon-a-Group1312316358" /></div>
                <div className="find-route-selects">
                    <label>始发地<ShipRemotePortSelect value={origin?.id} placeholder="请输入始发地" popupClassName="find-route-port-dropdown" maxSearchLength={100} onChange={(_value, port) => setOrigin(Array.isArray(port) ? port[0] : port)} /></label>
                    <label>目的地<ShipRemotePortSelect value={destination?.id} placeholder="请输入目的地" popupClassName="find-route-port-dropdown" maxSearchLength={100} onChange={(_value, port) => setDestination(Array.isArray(port) ? port[0] : port)} /></label>
                </div>
            </div>
            <div className="find-route-estimates">
                <label><span>预计航速</span><Input type="number" min={1} max={300} step={1} value={speed} suffix="节" onChange={event => { const value = Number.parseInt(event.target.value, 10); setSpeed(event.target.value === "" ? undefined : Math.min(300, Math.max(1, Number.isNaN(value) ? 1 : value))); }} /></label>
                <label><span>预计出发时间</span><DatePicker showTime allowClear value={departureTime} onChange={setDepartureTime} format="YYYY-MM-DD HH:mm:ss" /></label>
            </div>
            <Button type="primary" loading={loading} onClick={() => void plan()}>{loading ? "规划中..." : "开始规划"}</Button>
        </div>
        {result ? <div className="find-route-planning-result"><strong>计算结果</strong><div className="find-route-planning-result-content"><p><span>总航程</span><b>{result.distance || EMPTY_VALUE} nm</b></p><p><span>预计航行用时</span><b>{result.duration ? <>{Math.floor(result.duration / 24) ? `${Math.floor(result.duration / 24)}天` : ""}{(result.duration % 24).toFixed(2)}小时</> : EMPTY_VALUE}</b></p><p><span>预计到达时间</span><b>{result.arrivalTime || EMPTY_VALUE}</b></p></div></div> : null}
    </aside>;
}
